"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { listPlaybooks, type PlaybookSummary } from "@/lib/api";
import { formatPct, relativeTime } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RequireRole } from "@/components/auth/require-role";

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function answerRate(pb: PlaybookSummary): string {
  return formatPct(pb.answered_count, pb.dialed_count);
}

function successRate(pb: PlaybookSummary): string {
  return formatPct(pb.success_count, pb.answered_count);
}

export default function PlaybooksPage() {
  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([]);

  useEffect(() => {
    listPlaybooks().then((res) => setPlaybooks(res.playbooks));
  }, []);

  return (
    <div className="flex min-h-full flex-col p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">话术</h1>
        <RequireRole roles={["owner"]}>
          <Button asChild size="sm">
            <Link href="/playbooks/create">创建</Link>
          </Button>
        </RequireRole>
      </div>

      {playbooks.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          暂无话术。
          <RequireRole roles={["owner"]}>
            {" "}
            <Link href="/playbooks/create" className="ml-1 underline">
              立即创建
            </Link>
          </RequireRole>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {playbooks.map((pb) => (
            <Link key={pb.id} href={`/playbooks/${pb.id}`}>
              <Card className="h-full transition-colors hover:bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-base">{pb.name}</CardTitle>
                  {pb.description && (
                    <CardDescription className="line-clamp-2">
                      {pb.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <Metric
                      value={pb.total_sessions > 0 ? pb.total_sessions.toLocaleString() : "\u2014"}
                      label="会话数"
                    />
                    <Metric value={answerRate(pb)} label="接通率" />
                    <Metric value={successRate(pb)} label="成功率" />
                  </div>
                </CardContent>
                <CardFooter className="flex-col items-start gap-2">
                  <div className="flex w-full items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {pb.voice_id
                        ? (pb.voices.find((v) => v.key === pb.voice_id)?.name ?? pb.voice_id)
                        : "未绑定声音"}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {pb.last_active ? relativeTime(pb.last_active) : "暂无记录"}
                    </span>
                  </div>
                  <div className="flex w-full items-center gap-2">
                    <Badge variant="secondary">
                      {pb.target_count} 个目标
                    </Badge>
                    {pb.phase_count > 0 && (
                      <Badge variant="outline">
                        {pb.phase_count} 个阶段
                      </Badge>
                    )}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {pb.id}
                    </span>
                  </div>
                </CardFooter>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
